import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PriorityBadge } from "../components/PriorityBadge";
import { StatusBadge } from "../components/StatusBadge";
import { TaskForm } from "../components/TaskForm";
import type { ApiResponse, CreateTaskInput, Task } from "../types";

function formatDate(dateString: string | null): string {
	if (!dateString) return "-";
	const date = new Date(dateString);
	return date.toLocaleDateString("ja-JP", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

function formatDateTime(dateString: string): string {
	const date = new Date(dateString);
	return date.toLocaleString("ja-JP", {
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function TaskDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const [task, setTask] = useState<Task | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		const fetchTask = async () => {
			if (!id) return;

			setIsLoading(true);
			setError(null);

			try {
				const response = await fetch(`/api/tasks/${id}`);
				const data: ApiResponse<Task> = await response.json();

				if (data.success && data.data) {
					setTask(data.data);
				} else {
					setError(data.error || "タスクの取得に失敗しました");
				}
			} catch (err) {
				setError("サーバーとの通信に失敗しました");
				console.error("Failed to fetch task:", err);
			} finally {
				setIsLoading(false);
			}
		};

		fetchTask();
	}, [id]);

	const handleUpdateTask = async (data: CreateTaskInput) => {
		if (!id) return;

		setIsSubmitting(true);
		setError(null);

		try {
			const response = await fetch(`/api/tasks/${id}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			});
			const result: ApiResponse<Task> = await response.json();

			if (result.success && result.data) {
				setTask(result.data);
				setIsEditing(false);
			} else {
				setError(result.error || "タスクの更新に失敗しました");
			}
		} catch (err) {
			setError("サーバーとの通信に失敗しました");
			console.error("Failed to update task:", err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDeleteTask = async () => {
		if (!id || !window.confirm("このタスクを削除しますか?")) return;

		setIsDeleting(true);
		setError(null);

		try {
			const response = await fetch(`/api/tasks/${id}`, {
				method: "DELETE",
			});
			const result: ApiResponse<null> = await response.json();

			if (result.success) {
				navigate("/tasks", { replace: true });
			} else {
				setError(result.error || "タスクの削除に失敗しました");
			}
		} catch (err) {
			setError("サーバーとの通信に失敗しました");
			console.error("Failed to delete task:", err);
		} finally {
			setIsDeleting(false);
		}
	};

	if (isLoading) {
		return (
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="flex items-center justify-center py-12">
					<div className="flex flex-col items-center gap-4">
						<svg
							className="animate-spin h-8 w-8 text-primary-600"
							fill="none"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
							/>
							<path
								className="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							/>
						</svg>
						<p className="text-gray-600">読み込み中...</p>
					</div>
				</div>
			</div>
		);
	}

	if (error && !task) {
		return (
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="card p-12 text-center">
					<svg
						className="w-16 h-16 mx-auto text-red-400 mb-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
						/>
					</svg>
					<h2 className="text-lg font-medium text-gray-900 mb-2">タスクが見つかりません</h2>
					<p className="text-gray-600 mb-6">{error}</p>
					<Link to="/tasks" className="btn-primary">
						タスク一覧に戻る
					</Link>
				</div>
			</div>
		);
	}

	if (!task) return null;

	return (
		<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
			<nav className="mb-6">
				<Link
					to="/tasks"
					className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
				>
					<svg
						className="w-5 h-5 mr-1"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					タスク一覧に戻る
				</Link>
			</nav>

			{error && (
				<div
					className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
					role="alert"
				>
					<div className="flex items-center gap-2">
						<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
							<path
								fillRule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
								clipRule="evenodd"
							/>
						</svg>
						<span>{error}</span>
					</div>
				</div>
			)}

			<div className="card">
				{isEditing ? (
					<div className="p-6">
						<h2 className="text-lg font-semibold text-gray-900 mb-4">タスクを編集</h2>
						<TaskForm
							task={task}
							onSubmit={handleUpdateTask}
							onCancel={() => setIsEditing(false)}
							isSubmitting={isSubmitting}
						/>
					</div>
				) : (
					<>
						<div className="p-6 border-b border-gray-200">
							<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
								<h1 className="text-2xl font-bold text-gray-900">{task.title}</h1>
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={() => setIsEditing(true)}
										className="btn-secondary"
									>
										<svg
											className="w-4 h-4 mr-1.5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
											/>
										</svg>
										編集
									</button>
									<button
										type="button"
										onClick={handleDeleteTask}
										disabled={isDeleting}
										className="btn-danger"
									>
										{isDeleting ? (
											<span className="flex items-center gap-2">
												<svg
													className="animate-spin h-4 w-4"
													fill="none"
													viewBox="0 0 24 24"
													aria-hidden="true"
												>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
													/>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													/>
												</svg>
												削除中...
											</span>
										) : (
											<>
												<svg
													className="w-4 h-4 mr-1.5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													aria-hidden="true"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
													/>
												</svg>
												削除
											</>
										)}
									</button>
								</div>
							</div>

							<div className="flex flex-wrap items-center gap-2 mt-4">
								<StatusBadge status={task.status} />
								<PriorityBadge priority={task.priority} />
							</div>
						</div>

						<div className="p-6 space-y-6">
							<div>
								<h2 className="text-sm font-medium text-gray-500 mb-2">説明</h2>
								{task.description ? (
									<p className="text-gray-900 whitespace-pre-wrap">{task.description}</p>
								) : (
									<p className="text-gray-400 italic">説明なし</p>
								)}
							</div>

							<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
								<div>
									<h3 className="text-sm font-medium text-gray-500 mb-1">期限</h3>
									<p className="text-gray-900">{formatDate(task.dueDate)}</p>
								</div>
								<div>
									<h3 className="text-sm font-medium text-gray-500 mb-1">作成日時</h3>
									<p className="text-gray-900">{formatDateTime(task.createdAt)}</p>
								</div>
								<div>
									<h3 className="text-sm font-medium text-gray-500 mb-1">更新日時</h3>
									<p className="text-gray-900">{formatDateTime(task.updatedAt)}</p>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
